import { useCallback, useLayoutEffect, useRef, useState } from 'react'

/**
 * Does a line-clamped element actually overflow its clamp?
 *
 * Returns a callback ref to put on the clamped element and a boolean saying
 * whether its content is taller than the clamp allows — i.e. whether a
 * "Show more" / "Read more" affordance is warranted at all. An affordance that
 * appears on text which is entirely visible is the one outcome this exists to
 * prevent.
 *
 * ## Why this is a hook and not a character count
 *
 * Estimating from `body.length` was the first thing tried in `AnnouncementCard`
 * and cannot work: the wrap point depends on the rendered font, the column
 * width and where the author put their newlines, so any character threshold is
 * wrong for some real content in BOTH directions. `scrollHeight > clientHeight`
 * asks the browser the question directly and is right by construction.
 *
 * ## Why it is shared
 *
 * `AnnouncementCard` had ~45 lines of this, reasoned out the hard way, and the
 * product page's clamped description needed the same measurement with a
 * different affordance (expand in place rather than navigate to a detail page).
 * Per working-conventions §36 the second hand-roll is the trigger to extract —
 * and the shared unit is the MEASUREMENT, not the whole component, precisely
 * because the two callers do different things with the answer.
 *
 * Four details below are load-bearing and were all learned from a real failure.
 * Do not simplify them away.
 */
export const useClampOverflow = (
  /**
   * `false` when the caller is in a state that never clamps (an expanded view,
   * a variant without a clamp). The ref is still returned and still attaches —
   * `overflows` is simply forced to `false`, so a stale `true` cannot survive a
   * variant change in place.
   */
  enabled: boolean,
  /**
   * The content itself. A dependency because the same mounted element can be
   * handed different content without remounting, and a stale measurement here
   * is both invisible and misleading.
   */
  content?: unknown,
) => {
  const ref = useRef<HTMLElement | null>(null)
  const [overflows, setOverflows] = useState(false)

  /*
    (1) A CALLBACK ref, not the ref object handed straight to `ref=`. MUI types
    `Typography`'s ref against the concrete element its `component` resolves to,
    so a `RefObject<HTMLElement>` is rejected as too wide (refs are invariant).
    A callback taking `HTMLElement | null` IS assignable, because parameters go
    the other way — the narrower element the component passes satisfies the
    wider parameter. Same element, same effect, and it typechecks without an
    assertion.
  */
  const setRef = useCallback((el: HTMLElement | null) => {
    ref.current = el
  }, [])

  const measure = useCallback((el: HTMLElement | null) => {
    if (!el) return
    /*
      (2) 1px of slack. Sub-pixel line-box rounding makes `scrollHeight` exceed
      `clientHeight` by a fraction on content that fits EXACTLY, which would put
      a "Show more" on an element whose text is entirely visible.
    */
    setOverflows(el.scrollHeight - el.clientHeight > 1)
  }, [])

  useLayoutEffect(() => {
    const el = ref.current
    if (!enabled || !el) {
      setOverflows(false)
      return
    }

    measure(el)

    /*
      (3) Re-measured on RESIZE, not just on mount. A clamped column is capped
      but rarely fixed — narrowing the window rewraps the content, and text that
      fitted three lines at 1100px may not at 700px. Without the observer the
      element keeps a stale answer and silently truncates with no way to reach
      the rest.

      (4) Guarded: ResizeObserver is universally available in the browsers this
      app supports, but NOT in jsdom, where its absence would throw during a
      test render rather than merely skipping the re-measure.
    */
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure(el))
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled, content, measure])

  return { ref: setRef, overflows }
}
